import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { Base64ToObject, ObjectToBase64 } from '../../modules/lower-ui-kit/lib/serializers/serializer-utils';

interface UseSessionStateProps <S>{
  key: string;
  initialState: S | (() => S);
}

/**
 * Returns a stateful value, and a function to update it.
 * Initial value is pulled from session storage if available, and values are saved
 * to session storage when updated.
 * Much this code is pulled from https://usehooks.com/useLocalStorage/.
 *
 * @param key
 * @param initialState
 */
function useSessionState<S>({
  key,
  initialState,
}: UseSessionStateProps<S>): [S, Dispatch<SetStateAction<S>>] {
  const [encodedKey, _] = useState(btoa(key));

  // @ts-expect-error Complicated issue with generics and useState
  const [sessionState, setState] = useState<S>(() => {
    const storedValue = window.sessionStorage.getItem(encodedKey);

    if (!storedValue) {
      window.sessionStorage.setItem(encodedKey, ObjectToBase64(initialState));

      return initialState;
    }

    return Base64ToObject(storedValue);
  });

  const setSessionState = useCallback((value: S | ((val: S) => S)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function
        ? value(sessionState)
        : value;

      setState(valueToStore);
      window.sessionStorage.setItem(encodedKey, ObjectToBase64(valueToStore));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.trace(error);
    }
  }, [encodedKey, sessionState]);

  return [sessionState, setSessionState];
}

export default useSessionState;
